import React, { useState, useEffect } from 'react';
import { useLanguage } from '@/contexts/LanguageContext';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { 
  BookOpen, 
  FileText, 
  Upload, 
  Download, 
  Trash2,
  ChevronRight,
  FolderOpen,
  File,
  Copy,
  Plus
} from 'lucide-react';
import { toast } from 'sonner';
import api from '@/utils/api';

const ComplianceLibrary = () => {
  const { t } = useLanguage();
  const [structure, setStructure] = useState({});
  const [selectedState, setSelectedState] = useState(null);
  const [selectedType, setSelectedType] = useState(null);
  const [selectedEntity, setSelectedEntity] = useState(null);
  const [files, setFiles] = useState([]);
  const [projects, setProjects] = useState([]);
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [uploading, setUploading] = useState(false);

  useEffect(() => {
    loadLibraryStructure();
    loadProjects();
  }, []);

  const loadLibraryStructure = async () => {
    try {
      const response = await api.get('/compliance-library/structure');
      setStructure(response.data);
    } catch (error) {
      toast.error('Error loading library');
      console.error(error);
    }
  };

  const loadProjects = async () => {
    try {
      const response = await api.get('/file-manager/projects');
      setProjects(response.data);
    } catch (error) {
      console.error(error);
    }
  };

  const handleSelectState = (state) => {
    setSelectedState(state);
    setSelectedType(null);
    setSelectedEntity(null);
    setFiles([]);
  };

  const handleSelectType = (type) => {
    setSelectedType(type);
    setSelectedEntity(null);
    setFiles([]);
  };

  const handleSelectEntity = (entity) => {
    setSelectedEntity(entity);
    if (selectedState && selectedType && entity) {
      setFiles(structure[selectedState][selectedType][entity] || []);
    }
  };

  const handleCopyToProject = async (fileId, projectId) => {
    try {
      await api.post(`/compliance-library/copy-to-project/${fileId}/${projectId}`);
      toast.success('File copied to project successfully');
    } catch (error) {
      toast.error('Error copying file to project');
      console.error(error);
    }
  };

  const handleFileUpload = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    if (!selectedState || !selectedType || !selectedEntity) {
      toast.error('Please select state, construction type, and regulatory entity');
      return;
    }

    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('state', selectedState);
      formData.append('construction_type', selectedType);
      formData.append('regulatory_entity', selectedEntity);

      await api.post('/compliance-library/upload', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });

      toast.success('File uploaded successfully');
      loadLibraryStructure();
      // Reload current view
      if (selectedState && selectedType && selectedEntity) {
        const response = await api.get('/compliance-library/structure');
        setFiles(response.data[selectedState][selectedType][selectedEntity] || []);
      }
    } catch (error) {
      toast.error('Error uploading file');
      console.error(error);
    } finally {
      setUploading(false);
    }
  };

  const handleDownload = (fileId) => {
    window.open(`${process.env.REACT_APP_BACKEND_URL}/api/compliance-library/download/${fileId}`, '_blank');
  };

  const states = Object.keys(structure);
  const constructionTypes = selectedState ? Object.keys(structure[selectedState]) : [];
  const entities = selectedState && selectedType ? Object.keys(structure[selectedState][selectedType]) : [];

  return (
    <div className="container mx-auto px-6 py-8">
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-slate-900 dark:text-white mb-2">
          {t('complianceLibrary') || 'Compliance Library'}
        </h1>
        <p className="text-slate-600 dark:text-slate-400">
          {t('complianceLibraryDesc') || 'Access building codes and regulations by state and type'}
        </p>
      </div>

      <div className="grid grid-cols-12 gap-6">
        {/* Navigation Sidebar */}
        <div className="col-span-3 space-y-4">
          {/* States */}
          <Card className="p-4 border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800">
            <h3 className="font-semibold text-slate-900 dark:text-white mb-3 flex items-center">
              <FolderOpen className="mr-2 h-5 w-5" />
              {t('states') || 'States'}
            </h3>
            <div className="space-y-1 max-h-[200px] overflow-y-auto">
              {states.length === 0 ? (
                <p className="text-sm text-slate-500 text-center py-4">
                  {t('noStates') || 'No states yet'}
                </p>
              ) : (
                states.map((state) => (
                  <div
                    key={state}
                    onClick={() => handleSelectState(state)}
                    className={`p-2 rounded cursor-pointer transition-all ${
                      selectedState === state
                        ? 'bg-blue-50 dark:bg-blue-900/20 text-blue-600 font-semibold'
                        : 'hover:bg-slate-100 dark:hover:bg-slate-700 text-slate-700 dark:text-slate-300'
                    }`}
                  >
                    <div className="flex items-center justify-between">
                      <span className="text-sm">{state}</span>
                      <ChevronRight className="h-4 w-4" />
                    </div>
                  </div>
                ))
              )}
            </div>
          </Card>

          {/* Construction Types */}
          {selectedState && (
            <Card className="p-4 border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800">
              <h3 className="font-semibold text-slate-900 dark:text-white mb-3 flex items-center">
                <FolderOpen className="mr-2 h-5 w-5" />
                {t('constructionTypes') || 'Construction Types'}
              </h3>
              <div className="space-y-1 max-h-[200px] overflow-y-auto">
                {constructionTypes.map((type) => (
                  <div
                    key={type}
                    onClick={() => handleSelectType(type)}
                    className={`p-2 rounded cursor-pointer transition-all ${
                      selectedType === type
                        ? 'bg-blue-50 dark:bg-blue-900/20 text-blue-600 font-semibold'
                        : 'hover:bg-slate-100 dark:hover:bg-slate-700 text-slate-700 dark:text-slate-300'
                    }`}
                  >
                    <div className="flex items-center justify-between">
                      <span className="text-sm">{type.replace(/_/g, ' ')}</span>
                      <ChevronRight className="h-4 w-4" />
                    </div>
                  </div>
                ))}
              </div>
            </Card>
          )}

          {/* Regulatory Entities */}
          {selectedType && (
            <Card className="p-4 border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800">
              <h3 className="font-semibold text-slate-900 dark:text-white mb-3 flex items-center">
                <BookOpen className="mr-2 h-5 w-5" />
                {t('regulatoryEntities') || 'Regulatory Entities'}
              </h3>
              <div className="space-y-1 max-h-[200px] overflow-y-auto">
                {entities.map((entity) => (
                  <div
                    key={entity}
                    onClick={() => handleSelectEntity(entity)}
                    className={`p-2 rounded cursor-pointer transition-all ${
                      selectedEntity === entity
                        ? 'bg-blue-50 dark:bg-blue-900/20 text-blue-600 font-semibold'
                        : 'hover:bg-slate-100 dark:hover:bg-slate-700 text-slate-700 dark:text-slate-300'
                    }`}
                  >
                    <span className="text-sm">{entity}</span>
                  </div>
                ))}
              </div>
            </Card>
          )}
        </div>

        {/* Files Area */}
        <div className="col-span-9">
          {!selectedEntity ? (
            <Card className="p-12 text-center border-2 border-dashed border-slate-300 dark:border-slate-600">
              <BookOpen className="mx-auto h-24 w-24 text-slate-300 mb-6" />
              <h2 className="text-2xl font-bold text-slate-900 dark:text-white mb-4">
                {t('selectCategory') || 'Select Category'}
              </h2>
              <p className="text-slate-600 dark:text-slate-400 max-w-md mx-auto">
                {t('selectCategoryDesc') || 'Choose a state, construction type, and regulatory entity to view compliance files'}
              </p>
            </Card>
          ) : (
            <>
              {/* Breadcrumb */}
              <div className="mb-4 flex items-center space-x-2 text-sm text-slate-600 dark:text-slate-400">
                <span className="font-semibold">{selectedState}</span>
                <ChevronRight className="h-4 w-4" />
                <span className="font-semibold">{selectedType.replace(/_/g, ' ')}</span>
                <ChevronRight className="h-4 w-4" />
                <span className="font-semibold text-blue-600">{selectedEntity}</span>
              </div>

              {/* Toolbar */}
              <Card className="p-4 border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 mb-6">
                <div className="flex items-center justify-between">
                  <h3 className="text-lg font-semibold text-slate-900 dark:text-white">
                    {files.length} {t('files') || 'Files'}
                  </h3>
                  <div>
                    <input
                      type="file"
                      id="compliance-upload"
                      className="hidden"
                      onChange={handleFileUpload}
                      disabled={uploading}
                      accept=".pdf"
                    />
                    <Button
                      onClick={() => document.getElementById('compliance-upload').click()}
                      disabled={uploading}
                      className="bg-blue-600 hover:bg-blue-700"
                    >
                      <Upload className="mr-2 h-4 w-4" />
                      {uploading ? (t('uploading') || 'Uploading...') : (t('uploadFile') || 'Upload File')}
                    </Button>
                  </div>
                </div>
              </Card>

              {/* Files Grid */}
              {files.length === 0 ? (
                <Card className="p-12 text-center border-2 border-dashed border-slate-300 dark:border-slate-600">
                  <FileText className="mx-auto h-24 w-24 text-slate-300 mb-6" />
                  <h3 className="text-xl font-bold text-slate-900 dark:text-white mb-4">
                    {t('noFilesYet') || 'No files yet'}
                  </h3>
                  <p className="text-slate-600 dark:text-slate-400 max-w-md mx-auto mb-6">
                    {t('uploadFirstComplianceFile') || 'Upload your first compliance file to this category'}
                  </p>
                  <Button
                    onClick={() => document.getElementById('compliance-upload').click()}
                    className="bg-blue-600 hover:bg-blue-700"
                  >
                    <Upload className="mr-2 h-4 w-4" />
                    {t('uploadFile') || 'Upload File'}
                  </Button>
                </Card>
              ) : (
                <div className="space-y-3">
                  {files.map((file) => (
                    <Card
                      key={file.id}
                      className="p-4 border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 hover:shadow-lg transition-shadow"
                    >
                      <div className="flex items-center justify-between">
                        <div className="flex items-center space-x-4 flex-1">
                          <div className="p-2 bg-blue-50 dark:bg-blue-900/20 rounded">
                            <FileText className="h-6 w-6 text-blue-600" />
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className="text-sm font-semibold text-slate-900 dark:text-white">
                              {file.original_filename}
                            </p>
                            {file.description && (
                              <p className="text-xs text-slate-500 mt-1">{file.description}</p>
                            )}
                            <div className="flex items-center space-x-4 mt-2 text-xs text-slate-500">
                              <span>{(file.file_size / 1024).toFixed(1)} KB</span>
                              {file.code_year && <span>Year: {file.code_year}</span>}
                              <span>{new Date(file.uploaded_at).toLocaleDateString()}</span>
                            </div>
                          </div>
                        </div>

                        <div className="flex items-center space-x-2">
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => handleDownload(file.id)}
                          >
                            <Download className="mr-2 h-4 w-4" />
                            {t('download') || 'Download'}
                          </Button>
                          
                          {projects.length > 0 && (
                            <select
                              className="px-3 py-2 border border-slate-200 dark:border-slate-700 rounded text-sm"
                              onChange={(e) => {
                                if (e.target.value) {
                                  handleCopyToProject(file.id, e.target.value);
                                  e.target.value = '';
                                }
                              }}
                            >
                              <option value="">{t('copyToProject') || 'Copy to Project...'}</option>
                              {projects.map((project) => (
                                <option key={project.project_id} value={project.project_id}>
                                  {project.project_name}
                                </option>
                              ))}
                            </select>
                          )}
                        </div>
                      </div>
                    </Card>
                  ))}
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default ComplianceLibrary;
